import { sql } from "drizzle-orm";
import { pgTable, text, varchar, integer, boolean, timestamp, jsonb, index } from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
import type { z } from "zod";

import { tenants, companies, areas, sites, projects, regions } from './common';

export const retentionPolicies = pgTable("retention_policies", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  policyName: text("policy_name").notNull(),
  applicableEntityTypes: text("applicable_entity_types").array().default([]),
  retentionBasis: text("retention_basis"),
  duration: text("duration"),
  archiveTrigger: text("archive_trigger"),
  deletionEligibilityRules: jsonb("deletion_eligibility_rules").$type<Record<string, any>>().default({}),
  legalHoldSupport: boolean("legal_hold_support").notNull().default(false),
  confidentialityHandling: text("confidentiality_handling"),
  reviewBeforeDestruction: boolean("review_before_destruction").notNull().default(true),
});
export const insertRetentionPolicySchema = createInsertSchema(retentionPolicies).omit({ id: true, tenantId: true });
export type InsertRetentionPolicy = z.infer<typeof insertRetentionPolicySchema>;
export type RetentionPolicy = typeof retentionPolicies.$inferSelect;

// ─── Document Taxonomy: Categories ──────────────────────────────────
export const documentCategories = pgTable("document_categories", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  name: text("name").notNull(),
  description: text("description"),
  parentId: text("parent_id"),
  sortOrder: integer("sort_order").notNull().default(0),
  active: boolean("active").notNull().default(true),
});
export const insertDocumentCategorySchema = createInsertSchema(documentCategories).omit({ id: true, tenantId: true });
export type InsertDocumentCategory = z.infer<typeof insertDocumentCategorySchema>;
export type DocumentCategory = typeof documentCategories.$inferSelect;

// ─── Document Taxonomy: Document Type Definitions ───────────────────
export const documentTypeDefinitions = pgTable("document_type_definitions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  name: text("name").notNull(),
  description: text("description"),
  categoryId: text("category_id"),
  defaultConfidentiality: text("default_confidentiality"),
  requiredMetadataFields: text("required_metadata_fields").array().default([]),
  status: text("status").notNull().default("active"),
  active: boolean("active").notNull().default(true),
});
export const insertDocumentTypeDefinitionSchema = createInsertSchema(documentTypeDefinitions).omit({ id: true, tenantId: true });
export type InsertDocumentTypeDefinition = z.infer<typeof insertDocumentTypeDefinitionSchema>;
export type DocumentTypeDefinition = typeof documentTypeDefinitions.$inferSelect;

// ─── Document Taxonomy: Confidentiality Labels ──────────────────────
export const confidentialityLabels = pgTable("confidentiality_labels", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  name: text("name").notNull(),
  levelNumber: integer("level_number").notNull().default(1),
  description: text("description"),
  color: text("color").notNull().default("#6b7280"),
});
export const insertConfidentialityLabelSchema = createInsertSchema(confidentialityLabels).omit({ id: true, tenantId: true });
export type InsertConfidentialityLabel = z.infer<typeof insertConfidentialityLabelSchema>;
export type ConfidentialityLabel = typeof confidentialityLabels.$inferSelect;

// ─── Document Taxonomy: Metadata Field Definitions ──────────────────
export const metadataFieldDefinitions = pgTable("metadata_field_definitions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  name: text("name").notNull(),
  fieldType: text("field_type").notNull().default("text"),
  required: boolean("required").notNull().default(false),
  description: text("description"),
  options: jsonb("options").$type<string[]>().default([]),
});
export const insertMetadataFieldDefinitionSchema = createInsertSchema(metadataFieldDefinitions).omit({ id: true, tenantId: true });
export type InsertMetadataFieldDefinition = z.infer<typeof insertMetadataFieldDefinitionSchema>;
export type MetadataFieldDefinition = typeof metadataFieldDefinitions.$inferSelect;

// ─── Document Workflow Templates ────────────────────────────────────
export const documentWorkflowTemplates = pgTable("document_workflow_templates", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  name: text("name").notNull(),
  description: text("description"),
  status: text("status").notNull().default("active"),
  publicationRule: text("publication_rule"),
  acknowledgementRequired: boolean("acknowledgement_required").notNull().default(false),
  acknowledgementAudience: text("acknowledgement_audience"),
  acknowledgementDeadlineDays: integer("acknowledgement_deadline_days"),
  supersessionBehavior: text("supersession_behavior"),
  active: boolean("active").notNull().default(true),
});
export const insertDocumentWorkflowTemplateSchema = createInsertSchema(documentWorkflowTemplates).omit({ id: true, tenantId: true });
export type InsertDocumentWorkflowTemplate = z.infer<typeof insertDocumentWorkflowTemplateSchema>;
export type DocumentWorkflowTemplate = typeof documentWorkflowTemplates.$inferSelect;

// ─── Document Workflow Stages ───────────────────────────────────────
export const documentWorkflowStages = pgTable("document_workflow_stages", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  templateId: text("template_id").notNull(),
  name: text("name").notNull(),
  approverRole: text("approver_role"),
  durationDays: integer("duration_days").notNull().default(5),
  required: boolean("required").notNull().default(true),
  sortOrder: integer("sort_order").notNull().default(0),
});
export const insertDocumentWorkflowStageSchema = createInsertSchema(documentWorkflowStages).omit({ id: true, tenantId: true });
export type InsertDocumentWorkflowStage = z.infer<typeof insertDocumentWorkflowStageSchema>;
export type DocumentWorkflowStage = typeof documentWorkflowStages.$inferSelect;

// ─── Evidence Pack Rule Templates ───────────────────────────────────
export const evidencePackRuleTemplates = pgTable("evidence_pack_rule_templates", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  name: text("name").notNull(),
  description: text("description"),
  status: text("status").notNull().default("active"),
  packType: text("pack_type"),
  exportFormats: text("export_formats").array().default([]),
  active: boolean("active").notNull().default(true),
});
export const insertEvidencePackRuleTemplateSchema = createInsertSchema(evidencePackRuleTemplates).omit({ id: true, tenantId: true });
export type InsertEvidencePackRuleTemplate = z.infer<typeof insertEvidencePackRuleTemplateSchema>;
export type EvidencePackRuleTemplate = typeof evidencePackRuleTemplates.$inferSelect;

// ─── Evidence Pack Inclusion Rules ──────────────────────────────────
export const evidencePackInclusionRules = pgTable("evidence_pack_inclusion_rules", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  templateId: text("template_id").notNull(),
  sourceEntityType: text("source_entity_type").notNull(),
  requiredDocuments: text("required_documents").array().default([]),
  optional: boolean("optional").notNull().default(false),
  description: text("description"),
});
export const insertEvidencePackInclusionRuleSchema = createInsertSchema(evidencePackInclusionRules).omit({ id: true, tenantId: true });
export type InsertEvidencePackInclusionRule = z.infer<typeof insertEvidencePackInclusionRuleSchema>;
export type EvidencePackInclusionRule = typeof evidencePackInclusionRules.$inferSelect;

// ─── Documents ───────────────────────────────────────────────────────
export const documents = pgTable("documents", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  code: text("code"),
  title: text("title"),
  displayTitle: text("display_title"),
  type: text("type"),
  category: text("category"),
  status: text("status").notNull().default("draft"),

  controlled: boolean("controlled").notNull().default(false),
  external: boolean("external").notNull().default(false),
  compliance: boolean("compliance").notNull().default(false),
  policy: boolean("policy").notNull().default(false),
  procedure: boolean("procedure").notNull().default(false),
  classificationLabel: text("classification_label"),

  tenantScope: text("tenant_scope").references(() => tenants.id),
  regionScope: text("region_scope").references(() => regions.id),
  projectScope: text("project_scope").references(() => projects.id),
  siteScope: text("site_scope").references(() => sites.id),
  areaScope: text("area_scope").references(() => areas.id),
  companyScope: text("company_scope").references(() => companies.id),
  workerScope: text("worker_scope"),
  ptwScope: text("ptw_scope"),
  safetyCaseScope: text("safety_case_scope"),
  inspectionScope: text("inspection_scope"),

  ownerRole: text("owner_role"),
  owningTeam: text("owning_team"),
  documentController: text("document_controller"),
  confidentialityLevel: text("confidentiality_level").default("internal"),
  reviewRequired: boolean("review_required").notNull().default(false),
  approvalRequired: boolean("approval_required").notNull().default(false),
  acknowledgementRequired: boolean("acknowledgement_required").notNull().default(false),
  retentionPolicyId: text("retention_policy_id"),

  fileReference: text("file_reference"),
  mimeType: text("mime_type"),
  filename: text("filename"),
  checksum: text("checksum"),
  pageCount: integer("page_count"),
  language: text("language"),
  tags: text("tags").array().default([]),
  summary: text("summary"),

  effectiveDate: text("effective_date"),
  reviewDueDate: text("review_due_date"),
  expiryDate: text("expiry_date"),
  supersededBy: text("superseded_by"),
  currentVersionRef: text("current_version_ref"),
  archivedDate: text("archived_date"),

  createdBy: text("created_by"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  modifiedBy: text("modified_by"),
  modifiedDate: text("modified_date"),
  approvedBy: text("approved_by"),
  approvedDate: text("approved_date"),
  publishedBy: text("published_by"),
  publishedDate: text("published_date"),
  deletedAt: timestamp("deleted_at", { withTimezone: true }),
  deletedByUserId: text("deleted_by_user_id"),
});
export const insertDocumentSchema = createInsertSchema(documents).omit({ id: true, tenantId: true });
export type InsertDocument = z.infer<typeof insertDocumentSchema>;
export type Document = typeof documents.$inferSelect;

// ─── Document Versions ───────────────────────────────────────────────
export const documentVersions = pgTable("document_versions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  documentId: text("document_id").notNull(),
  versionNumber: integer("version_number").notNull(),
  status: text("status").notNull().default("draft"),
  fileName: text("file_name"),
  fileSize: integer("file_size"),
  mimeType: text("mime_type"),
  filePath: text("file_path"),
  fileReference: text("file_reference"),
  checksum: text("checksum"),
  changeSummary: text("change_summary"),
  changeNotes: text("change_notes"),
  effectiveDate: text("effective_date"),
  createdBy: text("created_by"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  uploadedBy: text("uploaded_by"),
  uploadedDate: text("uploaded_date"),
  supersededByVersion: text("superseded_by_version"),
});
export const insertDocumentVersionSchema = createInsertSchema(documentVersions).omit({ id: true, tenantId: true });
export type InsertDocumentVersion = z.infer<typeof insertDocumentVersionSchema>;
export type DocumentVersion = typeof documentVersions.$inferSelect;

// ─── Operational Records ─────────────────────────────────────────────
export const operationalRecords = pgTable("operational_records", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  code: text("code"),
  recordType: text("record_type").notNull(),
  title: text("title").notNull(),
  description: text("description"),
  status: text("status").notNull().default("draft"),

  tenantScope: text("tenant_scope"),
  projectScope: text("project_scope"),
  siteScope: text("site_scope"),
  areaScope: text("area_scope"),
  linkedEntityType: text("linked_entity_type"),
  linkedEntityId: text("linked_entity_id"),
  linkedWorkflowRef: text("linked_workflow_ref"),
  relatedWorkerRef: text("related_worker_ref"),
  relatedCompanyRef: text("related_company_ref"),
  relatedUserRef: text("related_user_ref"),
  relatedPtwRef: text("related_ptw_ref"),
  relatedEquipmentRef: text("related_equipment_ref"),
  relatedDocumentRef: text("related_document_ref"),

  structuredPayload: jsonb("structured_payload").$type<Record<string, any>>().default({}),
  generatedSummary: text("generated_summary"),
  supportingAttachments: jsonb("supporting_attachments").$type<Record<string, any>[]>().default([]),
  comments: jsonb("comments").$type<Record<string, any>[]>().default([]),
  decision: text("decision"),
  outcome: text("outcome"),
  executionStartTimestamp: text("execution_start_timestamp"),
  executionEndTimestamp: text("execution_end_timestamp"),

  autoOrManual: text("auto_or_manual").default("manual"),
  immutableAfterFinalization: boolean("immutable_after_finalization").notNull().default(true),
  approvalRequired: boolean("approval_required").notNull().default(false),
  signatureRequired: boolean("signature_required").notNull().default(false),
  retentionPolicy: text("retention_policy"),
  evidenceBearing: boolean("evidence_bearing").notNull().default(false),

  finalizedDate: text("finalized_date"),
  finalizedBy: text("finalized_by"),
  supersededByRef: text("superseded_by_ref"),
  sourceWorkflow: text("source_workflow"),
  sourceWorkflowId: text("source_workflow_id"),

  createdBy: text("created_by"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  modifiedBy: text("modified_by"),
  modifiedDate: text("modified_date"),
});
export const insertOperationalRecordSchema = createInsertSchema(operationalRecords).omit({ id: true, tenantId: true });
export type InsertOperationalRecord = z.infer<typeof insertOperationalRecordSchema>;
export type OperationalRecord = typeof operationalRecords.$inferSelect;

// ─── Form Templates ──────────────────────────────────────────────────
export const formTemplates = pgTable("form_templates", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  code: text("code"),
  name: text("name").notNull(),
  displayName: text("display_name"),
  description: text("description"),
  category: text("category"),
  status: text("status").notNull().default("draft"),
  version: integer("version").notNull().default(1),

  tenantScope: text("tenant_scope"),
  regionScope: text("region_scope"),
  projectScope: text("project_scope"),
  siteScope: text("site_scope"),
  areaScope: text("area_scope"),

  fields: jsonb("fields").$type<Record<string, any>[]>().default([]),
  sections: jsonb("sections").$type<Record<string, any>[]>().default([]),
  fieldTypes: jsonb("field_types").$type<Record<string, any>>().default({}),
  requiredRules: jsonb("required_rules").$type<Record<string, any>>().default({}),
  conditionalLogic: jsonb("conditional_logic").$type<Record<string, any>>().default({}),
  validationRules: jsonb("validation_rules").$type<Record<string, any>>().default({}),
  attachmentFields: jsonb("attachment_fields").$type<Record<string, any>[]>().default([]),
  signatureFields: jsonb("signature_fields").$type<Record<string, any>[]>().default([]),
  checklistBlocks: jsonb("checklist_blocks").$type<Record<string, any>[]>().default([]),
  repeatableSections: jsonb("repeatable_sections").$type<Record<string, any>[]>().default([]),

  saveDraftAllowed: boolean("save_draft_allowed").notNull().default(true),
  submitAction: text("submit_action"),
  approvalSteps: jsonb("approval_steps").$type<Record<string, any>[]>().default([]),
  reviewSteps: jsonb("review_steps").$type<Record<string, any>[]>().default([]),
  editAfterSubmitRules: jsonb("edit_after_submit_rules").$type<Record<string, any>>().default({}),
  finalizationRules: jsonb("finalization_rules").$type<Record<string, any>>().default({}),
  workflowConfig: jsonb("workflow_config").$type<Record<string, any>>().default({}),

  outputRecordType: text("output_record_type"),
  documentGenerationBehavior: text("document_generation_behavior"),
  owner: text("owner"),
  approverRoles: text("approver_roles").array().default([]),
  acknowledgementNeeded: boolean("acknowledgement_needed").notNull().default(false),
  retentionPolicy: text("retention_policy"),
  evidenceRelevance: boolean("evidence_relevance").notNull().default(false),

  createdBy: text("created_by"),
  updatedBy: text("updated_by"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
  publishedBy: text("published_by"),
  publishedDate: text("published_date"),
  retiredDate: text("retired_date"),
});
export const insertFormTemplateSchema = createInsertSchema(formTemplates).omit({ id: true, tenantId: true });
export type InsertFormTemplate = z.infer<typeof insertFormTemplateSchema>;
export type FormTemplate = typeof formTemplates.$inferSelect;

// ─── Form Template Versions ──────────────────────────────────────────
export const formTemplateVersions = pgTable("form_template_versions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  templateId: text("template_id").notNull(),
  versionNumber: integer("version_number").notNull(),
  fields: jsonb("fields").$type<Record<string, any>[]>().default([]),
  sections: jsonb("sections").$type<Record<string, any>[]>().default([]),
  workflowConfig: jsonb("workflow_config").$type<Record<string, any>>().default({}),
  publishedBy: text("published_by"),
  publishedDate: text("published_date"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
});
export const insertFormTemplateVersionSchema = createInsertSchema(formTemplateVersions).omit({ id: true, tenantId: true });
export type InsertFormTemplateVersion = z.infer<typeof insertFormTemplateVersionSchema>;
export type FormTemplateVersion = typeof formTemplateVersions.$inferSelect;

// ─── Form Submissions ────────────────────────────────────────────────
export const formSubmissions = pgTable("form_submissions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  templateId: text("template_id").notNull().references(() => formTemplates.id),
  templateVersion: integer("template_version"),
  status: text("status").notNull().default("draft"),
  submitter: text("submitter"),
  submitterName: text("submitter_name"),

  scopeContext: jsonb("scope_context").$type<Record<string, any>>().default({}),
  data: jsonb("data").$type<Record<string, any>>().default({}),
  structuredAnswers: jsonb("structured_answers").$type<Record<string, any>>().default({}),
  attachments: jsonb("attachments").$type<Record<string, any>[]>().default([]),
  signatures: jsonb("signatures").$type<Record<string, any>[]>().default([]),
  comments: jsonb("comments").$type<Record<string, any>[]>().default([]),
  linkedEntityRefs: jsonb("linked_entity_refs").$type<Record<string, any>[]>().default([]),
  outputRecordRef: text("output_record_ref"),
  outputDocumentRef: text("output_document_ref"),

  scope: text("scope"),
  projectId: text("project_id"),
  siteId: text("site_id"),
  areaId: text("area_id"),
  approverRoleRequired: text("approver_role_required"),
  assignedApproverId: text("assigned_approver_id"),
  approverName: text("approver_name"),
  approvalDate: text("approval_date"),
  approvalNotes: text("approval_notes"),
  rejectionReason: text("rejection_reason"),
  changeRequestNotes: text("change_request_notes"),
  generatedRecordId: text("generated_record_id"),
  generatedRecordType: text("generated_record_type"),

  submittedDate: text("submitted_date"),
  approvedDate: text("approved_date"),
  finalizedDate: text("finalized_date"),
  finalizedBy: text("finalized_by"),

  createdBy: text("created_by"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
  modifiedBy: text("modified_by"),
  modifiedDate: text("modified_date"),
});
export const insertFormSubmissionSchema = createInsertSchema(formSubmissions).omit({ id: true, tenantId: true });
export type InsertFormSubmission = z.infer<typeof insertFormSubmissionSchema>;
export type FormSubmission = typeof formSubmissions.$inferSelect;

// ─── Evidence Artifacts ──────────────────────────────────────────────
export const evidenceArtifacts = pgTable("evidence_artifacts", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  code: text("code"),
  evidenceType: text("evidence_type"),
  artifactType: text("artifact_type"),
  title: text("title"),
  description: text("description"),

  fileName: text("file_name"),
  fileType: text("file_type"),
  filePath: text("file_path"),
  fileReference: text("file_reference"),
  mimeType: text("mime_type"),
  filename: text("filename"),
  checksum: text("checksum"),
  fileSize: integer("file_size"),
  size: integer("size"),

  captureTimestamp: text("capture_timestamp"),
  uploadTimestamp: text("upload_timestamp"),
  capturedBy: text("captured_by"),
  capturedByName: text("captured_by_name"),
  uploadedBy: text("uploaded_by"),
  uploadedByName: text("uploaded_by_name"),
  sourceType: text("source_type"),
  originalSourceRef: text("original_source_ref"),
  deviceMetadata: jsonb("device_metadata").$type<Record<string, any>>().default({}),
  locationMetadata: jsonb("location_metadata").$type<Record<string, any>>().default({}),
  tags: text("tags").array().default([]),

  linkedEntityType: text("linked_entity_type"),
  linkedEntityId: text("linked_entity_id"),
  linkedRefs: jsonb("linked_refs").$type<Record<string, any>[]>().default([]),

  evidencePackEligible: boolean("evidence_pack_eligible").notNull().default(true),
  confidentialityLevel: text("confidentiality_level").default("internal"),
  integrityFlag: boolean("integrity_flag").notNull().default(true),
  integrityNotes: text("integrity_notes"),
  verificationStatus: text("verification_status").notNull().default("unverified"),
  verifiedFlag: boolean("verified_flag").notNull().default(false),
  verifiedBy: text("verified_by"),
  verifiedDate: text("verified_date"),
  redactionRequired: boolean("redaction_required").notNull().default(false),
  redactionNotes: text("redaction_notes"),
  retentionPolicy: text("retention_policy"),
  chainOfCustodyFlag: boolean("chain_of_custody_flag").notNull().default(false),

  projectId: text("project_id"),
  siteId: text("site_id"),

  createdBy: text("created_by"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
  modifiedBy: text("modified_by"),
  modifiedDate: text("modified_date"),
});
export const insertEvidenceArtifactSchema = createInsertSchema(evidenceArtifacts).omit({ id: true, tenantId: true });
export type InsertEvidenceArtifact = z.infer<typeof insertEvidenceArtifactSchema>;
export type EvidenceArtifact = typeof evidenceArtifacts.$inferSelect;

// ─── Evidence Links ──────────────────────────────────────────────────
export const evidenceLinks = pgTable("evidence_links", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  evidenceArtifactId: text("evidence_artifact_id").notNull(),
  linkedEntityType: text("linked_entity_type").notNull(),
  linkedEntityId: text("linked_entity_id").notNull(),
  linkPurpose: text("link_purpose"),
  linkedBy: text("linked_by"),
  linkedByName: text("linked_by_name"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
});
export const insertEvidenceLinkSchema = createInsertSchema(evidenceLinks).omit({ id: true, tenantId: true });
export type InsertEvidenceLink = z.infer<typeof insertEvidenceLinkSchema>;
export type EvidenceLink = typeof evidenceLinks.$inferSelect;

// ─── Evidence Packs ──────────────────────────────────────────────────
export const evidencePacks = pgTable("evidence_packs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  code: text("code"),
  title: text("title").notNull(),
  packType: text("pack_type"),
  status: text("status").notNull().default("draft"),

  scopeContext: jsonb("scope_context").$type<Record<string, any>>().default({}),
  sourceEntityType: text("source_entity_type"),
  sourceEntityId: text("source_entity_id"),
  reportingPeriod: text("reporting_period"),

  generatedBy: text("generated_by"),
  approvedBy: text("approved_by"),
  confidentialityLevel: text("confidentiality_level").default("internal"),
  exportFormats: text("export_formats").array().default([]),
  freezeSnapshot: boolean("freeze_snapshot").notNull().default(false),
  shareable: boolean("shareable").notNull().default(false),
  retentionPolicy: text("retention_policy"),

  missingRequiredItems: jsonb("missing_required_items").$type<Record<string, any>[]>().default([]),
  inclusionRules: jsonb("inclusion_rules").$type<Record<string, any>>().default({}),

  generatedDate: text("generated_date"),
  approvedDate: text("approved_date"),
  exportDate: text("export_date"),

  createdBy: text("created_by"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  modifiedBy: text("modified_by"),
  modifiedDate: text("modified_date"),
});
export const insertEvidencePackSchema = createInsertSchema(evidencePacks).omit({ id: true, tenantId: true });
export type InsertEvidencePack = z.infer<typeof insertEvidencePackSchema>;
export type EvidencePack = typeof evidencePacks.$inferSelect;

// ─── Evidence Pack Items ─────────────────────────────────────────────
export const evidencePackItems = pgTable("evidence_pack_items", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  packId: text("pack_id").notNull(),
  itemType: text("item_type").notNull(),
  itemId: text("item_id").notNull(),
  itemVersionRef: text("item_version_ref"),
  inclusionMethod: text("inclusion_method").default("manual"),
  includedBy: text("included_by"),
  includedDate: text("included_date"),
});
export const insertEvidencePackItemSchema = createInsertSchema(evidencePackItems).omit({ id: true, tenantId: true });
export type InsertEvidencePackItem = z.infer<typeof insertEvidencePackItemSchema>;
export type EvidencePackItem = typeof evidencePackItems.$inferSelect;

// ─── Approval Stages ────────────────────────────────────────────────
export const approvalStages = pgTable("approval_stages", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  targetEntityType: text("target_entity_type").notNull(),
  targetEntityId: text("target_entity_id").notNull(),
  targetVersion: text("target_version"),
  approvalStage: text("approval_stage"),
  approverRole: text("approver_role"),
  approverUser: text("approver_user"),
  status: text("status").notNull().default("pending"),
  comment: text("comment"),
  timestamp: text("timestamp"),
  // Typed decision timestamps added by migration 0153 (Task #715).
  // Supersede the legacy text `timestamp` field for new writes.
  approvedAt: timestamp("approved_at", { withTimezone: true }),
  rejectedAt: timestamp("rejected_at", { withTimezone: true }),
  nextStageLogic: jsonb("next_stage_logic").$type<Record<string, any>>().default({}),
}, (t) => ({
  // Partial indexes for querying recent approved/rejected decisions efficiently.
  idxApprovalStagesApprovedAt: index("idx_approval_stages_approved_at").on(t.tenantId, t.approvedAt).where(sql`approved_at IS NOT NULL`),
  idxApprovalStagesRejectedAt: index("idx_approval_stages_rejected_at").on(t.tenantId, t.rejectedAt).where(sql`rejected_at IS NOT NULL`),
}));
export const insertApprovalStageSchema = createInsertSchema(approvalStages).omit({ id: true, tenantId: true });
export type InsertApprovalStage = z.infer<typeof insertApprovalStageSchema>;
export type ApprovalStage = typeof approvalStages.$inferSelect;

// ─── Acknowledgements ───────────────────────────────────────────────
export const acknowledgements = pgTable("acknowledgements", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  targetDocumentId: text("target_document_id").notNull(),
  targetVersionId: text("target_version_id"),
  requiredAudienceScope: jsonb("required_audience_scope").$type<Record<string, any>>().default({}),
  recipientUser: text("recipient_user"),
  recipientRole: text("recipient_role"),
  recipientWorkerScope: text("recipient_worker_scope"),
  status: text("status").notNull().default("pending"),
  acknowledgedBy: text("acknowledged_by"),
  acknowledgedTimestamp: text("acknowledged_timestamp"),
  readViewedTimestamp: text("read_viewed_timestamp"),
  commentOrDeclaration: text("comment_or_declaration"),
  mandatoryByDate: text("mandatory_by_date"),
  escalationStatus: text("escalation_status"),
});
export const insertAcknowledgementSchema = createInsertSchema(acknowledgements).omit({ id: true, tenantId: true });
export type InsertAcknowledgement = z.infer<typeof insertAcknowledgementSchema>;
export type Acknowledgement = typeof acknowledgements.$inferSelect;

// ─── Document Audit Events ──────────────────────────────────────────
export const documentAuditEvents = pgTable("document_audit_events", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  eventType: text("event_type").notNull(),
  targetEntityType: text("target_entity_type").notNull(),
  targetEntityId: text("target_entity_id").notNull(),
  targetVersion: text("target_version"),
  actor: text("actor"),
  actorRole: text("actor_role"),
  actorName: text("actor_name"),
  timestamp: text("timestamp").notNull(),
  oldValue: jsonb("old_value").$type<Record<string, any>>(),
  newValue: jsonb("new_value").$type<Record<string, any>>(),
  changedFields: text("changed_fields").array().default([]),
  reason: text("reason"),
  notes: text("notes"),
  scopeContext: jsonb("scope_context").$type<Record<string, any>>().default({}),
  linkedApprovalId: text("linked_approval_id"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
});
export const insertDocumentAuditEventSchema = createInsertSchema(documentAuditEvents).omit({ id: true, tenantId: true });
export type InsertDocumentAuditEvent = z.infer<typeof insertDocumentAuditEventSchema>;
export type DocumentAuditEvent = typeof documentAuditEvents.$inferSelect;

// ─── Controlled Document Workflow States ─────────────────────────────
export const controlledDocumentWorkflowStates = pgTable("controlled_document_workflow_states", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  targetEntityType: text("target_entity_type").notNull(),
  targetEntityId: text("target_entity_id").notNull(),
  targetVersion: text("target_version"),
  currentState: text("current_state").notNull().default("draft"),
  previousState: text("previous_state"),
  transitionAction: text("transition_action"),
  transitionedBy: text("transitioned_by"),
  transitionedAt: text("transitioned_at"),
  assignedTo: text("assigned_to"),
  assignedRole: text("assigned_role"),
  dueDate: text("due_date"),
  comment: text("comment"),
  reason: text("reason"),
  scopeContext: jsonb("scope_context").$type<Record<string, any>>().default({}),
  stateMetadata: jsonb("state_metadata").$type<Record<string, any>>().default({}),
  createdBy: text("created_by"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
});
export const insertControlledDocumentWorkflowStateSchema = createInsertSchema(controlledDocumentWorkflowStates).omit({ id: true, tenantId: true });
export type InsertControlledDocumentWorkflowState = z.infer<typeof insertControlledDocumentWorkflowStateSchema>;
export type ControlledDocumentWorkflowState = typeof controlledDocumentWorkflowStates.$inferSelect;

// ─── Form & Evidence Audit Events ────────────────────────────────────
export const formEvidenceAuditEvents = pgTable("form_evidence_audit_events", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  eventType: text("event_type").notNull(),
  entityType: text("entity_type").notNull(),
  entityId: text("entity_id").notNull(),
  actorId: text("actor_id"),
  actorName: text("actor_name"),
  eventTimestamp: text("event_timestamp").notNull(),
  previousValues: jsonb("previous_values").$type<Record<string, any>>(),
  newValues: jsonb("new_values").$type<Record<string, any>>(),
  changedFields: text("changed_fields").array().default([]),
  reason: text("reason"),
  notes: text("notes"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
});
export const insertFormEvidenceAuditEventSchema = createInsertSchema(formEvidenceAuditEvents).omit({ id: true, tenantId: true });
export type InsertFormEvidenceAuditEvent = z.infer<typeof insertFormEvidenceAuditEventSchema>;
export type FormEvidenceAuditEvent = typeof formEvidenceAuditEvents.$inferSelect;

// ─── Retention Assignments ───────────────────────────────────────────
export const retentionAssignments = pgTable("retention_assignments", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  retentionPolicyId: text("retention_policy_id").notNull(),
  entityType: text("entity_type").notNull(),
  entityId: text("entity_id").notNull(),
  assignedBy: text("assigned_by"),
  assignedDate: text("assigned_date"),
  archiveEligibleDate: text("archive_eligible_date"),
  destructionEligibleDate: text("destruction_eligible_date"),
  status: text("status").notNull().default("active"),
  reviewedBy: text("reviewed_by"),
  reviewedDate: text("reviewed_date"),
  reviewOutcome: text("review_outcome"),
});
export const insertRetentionAssignmentSchema = createInsertSchema(retentionAssignments).omit({ id: true, tenantId: true });
export type InsertRetentionAssignment = z.infer<typeof insertRetentionAssignmentSchema>;
export type RetentionAssignment = typeof retentionAssignments.$inferSelect;

// ─── Legal Holds ─────────────────────────────────────────────────────
export const legalHolds = pgTable("legal_holds", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  name: text("name").notNull(),
  description: text("description"),
  reason: text("reason").notNull(),
  entityType: text("entity_type").notNull(),
  entityId: text("entity_id").notNull(),
  status: text("status").notNull().default("active"),
  issuedBy: text("issued_by"),
  issuedDate: text("issued_date"),
  releasedBy: text("released_by"),
  releasedDate: text("released_date"),
  notes: text("notes"),
});
export const insertLegalHoldSchema = createInsertSchema(legalHolds).omit({ id: true, tenantId: true });
export type InsertLegalHold = z.infer<typeof insertLegalHoldSchema>;
export type LegalHold = typeof legalHolds.$inferSelect;

// ─── Navigation Configuration ────────────────────────────────────────
